import mixpanel from 'mixpanel-browser';
import { hasAnalyticsConsent, subscribeToConsent } from './consent';
import { buildMarkers } from '@/utils/buildInfo';

/**
 * Mixpanel wrapper. Every public method funnels through `ready()`, which is the
 * consent gate: without a recorded "granted" the library is never initialised,
 * so no identifiers are created and no request leaves the browser. Consent is
 * re-checked on each call rather than only at init, so withdrawing it stops
 * tracking immediately instead of at the next reload.
 */
class Analytics {
  private initialized = false;

  init() {
    if (!this.initialized && hasAnalyticsConsent() && import.meta.env.VITE_MIXPANEL_TOKEN) {
      mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
        // debug: true,
        persistence: "localStorage",
        ignore_dnt: true,
        api_host: 'https://api-eu.mixpanel.com',
        // record_sessions_percent: 100,
        // record_heatmap_data: true,
      });
      // Super properties, so which build and which platform rides on every
      // event without a single call site having to remember. Registered here
      // rather than at a call site because the gap they close is that native
      // sessions were previously indistinguishable from web ones in Mixpanel.
      mixpanel.register(buildMarkers());
      this.initialized = true;
    }
  }

  private ready(): boolean {
    if (!hasAnalyticsConsent()) return false;
    this.init();
    return this.initialized;
  }

  /**
   * Consent was withdrawn: drop the distinct_id and super properties Mixpanel
   * persisted while it was granted. Clearing the store matters as much as
   * stopping the requests — leaving the identifier behind would keep the
   * visitor recognisable if they ever accepted again.
   */
  private forget() {
    if (this.initialized) {
      mixpanel.reset();
    }
    try {
      const keys = Object.keys(localStorage).filter((key) => key.startsWith('mp_'));
      keys.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage unavailable — nothing persisted to clear.
    }
  }

  track(event: string, properties?: any) {
    if (!this.ready()) return;
    mixpanel.track(event, properties);
  }

  trackErrorShown(surface: string, error: unknown, extras: Record<string, any> = {}) {
    this.track('Error Shown', {
      surface,
      ...extras,
      error: error instanceof Error ? error.message : String(error),
    });
  }

  identify(userId: string) {
    if (!this.ready()) return;
    mixpanel.identify(userId);
  }

  register(properties: any) {
    if (!this.ready()) return;
    mixpanel.register(properties);
  }

  reset() {
    if (!this.ready()) return;
    mixpanel.reset();
  }

  /** Wired at module scope; exposed for tests. */
  handleConsentChange() {
    if (!hasAnalyticsConsent()) {
      this.forget();
    }
  }
}

export const analytics = new Analytics();

subscribeToConsent(() => analytics.handleConsentChange());
